import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { newId } from '@abastece/shared';
import type { Proveedor } from '@abastece/core';
import { db } from '../db/dexie.js';
import { emitirYVer } from '../lib/emitir.js';
import { cambioDeProveedor, camposDeProveedor, type CamposProveedor } from '../lib/proveedores.js';
import { comoNombre } from '../lib/texto.js';
import { useT } from '../i18n/index.js';
import { Campo } from './Campo.js';
import { Ico } from './Iconos.js';

/**
 * A new supplier: the name, a phone and whom to ask for. «Proveedores» adds one with it, and
 * «Mercadería comprada» from its list of suppliers, in a sheet over the purchase, which then has it
 * picked (the owner, 2026-10-04). A name an active supplier already goes by is said, with that one to
 * pick where there is a purchase to pick it for: two of one supplier split what is owed to it in two.
 */
export function FormularioProveedor({
  nombreInicial = '',
  onCreado,
  onElegir,
}: {
  /** What was typed where a supplier was being looked for, and not found: their name, likely. */
  nombreInicial?: string;
  onCreado: (proveedorId: string) => void;
  /** The supplier already called that, picked instead: only where there is a purchase to pick it for. */
  onElegir?: (proveedorId: string) => void;
}) {
  const t = useT();
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const [nombre, setNombre] = useState(nombreInicial);
  const [telefono, setTelefono] = useState('');
  const [contacto, setContacto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const yaEsta = nombre.trim() ? proveedores.find((p) => p.activo && comoNombre(p.nombre) === comoNombre(nombre)) : undefined;

  async function guardar() {
    if (!nombre.trim() || yaEsta || guardando) return;
    setGuardando(true);
    setError(null);
    const proveedorId = newId();
    try {
      const { rechazo } = await emitirYVer('proveedor.creado', {
        proveedorId,
        nombre: nombre.trim(),
        ...(telefono.trim() ? { telefono: telefono.trim() } : {}),
        ...(contacto.trim() ? { contacto: contacto.trim() } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setNombre('');
      setTelefono('');
      setContacto('');
      onCreado(proveedorId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-formulario-proveedor>
      <Campo
        maxLength={120}
        etiqueta={t('comun.nombre')}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        autoFocus
        autoComplete="off"
        error={yaEsta ? t('prov.yaExiste', { nombre: yaEsta.nombre }) : null}
      />
      {yaEsta && onElegir && (
        <button type="button" className="btn-secundario" onClick={() => onElegir(yaEsta.id)} data-proveedor-elegir-ese>
          <Ico icono="listo" />
          {t('prov.elegirEse', { nombre: yaEsta.nombre })}
        </button>
      )}
      <Campo
        etiqueta={t('comun.telefono')}
        value={telefono}
        onChange={(e) => setTelefono(e.target.value)}
        inputMode="tel"
        maxLength={40}
        autoComplete="off"
      />
      <Campo etiqueta={t('prov.contacto')} value={contacto} onChange={(e) => setContacto(e.target.value)} autoComplete="off" maxLength={80} />
      {error && <p className="text-sm text-rose-400">{error}</p>}
      <button
        type="button"
        className="btn-primario"
        disabled={!nombre.trim() || Boolean(yaEsta) || guardando}
        onClick={() => void guardar()}
      >
        <Ico icono="guardar" />
        {t('prov.guardar')}
      </button>
    </div>
  );
}

/**
 * What the shop knows of a supplier, changed: the owner's (2026-10-16), from the supplier's page.
 * The fields of «Proveedor nuevo» as they stand, and the note, which only this form writes.
 * «Guardar cambios» is off until something changes, and sends only what did
 * (`cambioDeProveedor`); a field emptied is cleared. A name another active supplier already goes
 * by is said, as when adding one: two of one supplier split what is owed to it in two.
 */
export function EditarProveedor({ proveedor, onGuardado }: { proveedor: Proveedor; onGuardado: () => void }) {
  const t = useT();
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const [campos, setCampos] = useState(() => camposDeProveedor(proveedor));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const poner = (cambio: Partial<CamposProveedor>) => setCampos((c) => ({ ...c, ...cambio }));

  const yaEsta = campos.nombre.trim()
    ? proveedores.find((p) => p.activo && p.id !== proveedor.id && comoNombre(p.nombre) === comoNombre(campos.nombre))
    : undefined;
  const cambio = cambioDeProveedor(proveedor, campos);

  async function guardar() {
    if (!cambio || yaEsta || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('proveedor.actualizado', cambio);
      if (rechazo) {
        setError(rechazo);
        return;
      }
      onGuardado();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-editar-proveedor>
      <Campo
        maxLength={120}
        etiqueta={t('comun.nombre')}
        value={campos.nombre}
        onChange={(e) => poner({ nombre: e.target.value })}
        autoComplete="off"
        error={yaEsta ? t('prov.yaExiste', { nombre: yaEsta.nombre }) : campos.nombre.trim() ? null : t('prov.sinNombre')}
      />
      <Campo
        etiqueta={t('comun.telefono')}
        value={campos.telefono}
        onChange={(e) => poner({ telefono: e.target.value })}
        inputMode="tel"
        maxLength={40}
        autoComplete="off"
      />
      <Campo
        etiqueta={t('prov.contacto')}
        value={campos.contacto}
        onChange={(e) => poner({ contacto: e.target.value })}
        autoComplete="off"
        maxLength={80}
      />
      <Campo
        etiqueta={t('comun.nota')}
        value={campos.notas}
        onChange={(e) => poner({ notas: e.target.value })}
        autoComplete="off"
        maxLength={500}
      />
      {error && <p className="text-sm text-rose-400">{error}</p>}
      <button
        type="button"
        className="btn-primario"
        disabled={!cambio || Boolean(yaEsta) || guardando}
        onClick={() => void guardar()}
        data-sin-cambios={cambio ? undefined : ''}
      >
        <Ico icono="guardar" />
        {t('prov.guardarCambios')}
      </button>
    </div>
  );
}
